/**
 * FoliarSetupPage — the "Program setup" tab of the Foliar workspace (F4).
 *
 * Replaces the standalone NutritionProgrammeListPage as a workspace TAB: no
 * PageHeader (the layout already renders one — F1 Ruling 5), and the tab
 * merely adds the "Program setup" breadcrumb leaf.
 *
 * Two independently-actioned FormSection cards (spec §4.5):
 *   1. Programmes — every program for the tenant, with create/clone/delete.
 *   2. Young-tree program — a read-only summary of the WORKSPACE'S resolved
 *      programme (from the outlet context), not of any row selected below.
 *
 * The programmes list is loaded independently via nutritionService, mirroring
 * NutritionProgrammeListPage — the resolved outlet-context programme is used
 * ONLY by the young-tree card.
 *
 * GC 8 / defect #456: every DataTable column here has a mobile-card twin, and
 * the actions menu is rendered via ONE shared renderActions(row, testId) used
 * by both the table column and the card — the shipped #457 shape. Distinct
 * testids per call site (`programme-actions-table-*` / `programme-actions-card-*`)
 * because jsdom renders both simultaneously and a shared id is ambiguous.
 *
 * GC 2: DataTable's `emptyState` short-circuits the ENTIRE table, so
 * `New program` lives in an action bar OUTSIDE the DataTable.
 *
 * GC 6 (error-notice clearing is a list, not a habit): the page-level `error`
 * (load/clone/delete only — see below) is cleared at the START of onClone,
 * AND on the load's success path. onDelete does NOT clear it at entry:
 * window.confirm can be cancelled, and clearing before that prompt would
 * silently wipe an unrelated, still-valid error on a cancelled delete.
 * onClone/onDelete both reload the list on success via `.then(() => load())`.
 *
 * The create flow uses a SEPARATE `createError` state, not the page-level
 * `error`: `.pat-modal` is a fixed-position scrim with a focus trap
 * (Patterns.css), so a page-level Notice rendered behind an open modal is
 * invisible to a real user — the same invisible-notice bug class as the
 * earlier state-based variants, this time via CSS occlusion. `createError`
 * is cleared at the START of handleCreate AND when the modal (re)opens, and
 * set AFTER handleCreate's own `finally`-block reload settles, so that
 * reload's (page-level `error`) success clear cannot race it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, Field, FilterBar, FormSection, Modal, Notice, StatusPill } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FoliarWorkspaceContext } from './FoliarWorkspaceLayout';
import { nutritionService, NutritionProgramme } from '../../services/nutritionService';
import NutritionProgramActionsMenu from './NutritionProgramActionsMenu';
import { SetupRow, buildSetupRows, filterSetupRows, youngTreeSummary } from './foliarSetup';
import './nutrition.css';

const STATUS_VARIANT: Record<NutritionProgramme['status'], 'ok' | 'info' | 'warn'> = {
  active: 'ok',
  received: 'info',
  reviewed: 'warn',
  superseded: 'warn',
};

const editorPath = (id: string) => `/nutrition/foliar/setup/programmes/${id}`;

const FoliarSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // This page is a workspace TAB, so the layout hands it the resolved
  // programme — used only by the young-tree card. Null-safe because the
  // suite renders it standalone, and because useOutletContext returns
  // null/undefined outside an <Outlet>.
  const workspace = useOutletContext<FoliarWorkspaceContext | null>();
  const programmeId = workspace?.programme?._id;

  const [programmes, setProgrammes] = useState<NutritionProgramme[] | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'received'>('all');

  const [showNew, setShowNew] = useState(false);
  const [newSeason, setNewSeason] = useState('');
  const [newVendor, setNewVendor] = useState('');
  const [newContact, setNewContact] = useState('');
  const [creating, setCreating] = useState(false);
  // Separate from the page-level `error`: the create failure must render
  // INSIDE the modal dialog, not on the page behind it. `.pat-modal` is a
  // fixed-position scrim with a focus trap (Patterns.css), so a page-level
  // Notice is invisible to a real user while the modal is open — the same
  // bug class as the earlier state-based variants, this time via CSS
  // occlusion.
  const [createError, setCreateError] = useState('');

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    // Carry ?programme= or the layout falls back to the tenant's ACTIVE
    // programme and the crumb returns the user to a different board (#429).
    { label: t('nutrition.foliar.workspace.title'), to: programmeId ? `/nutrition/foliar?programme=${programmeId}` : '/nutrition/foliar' },
    { label: t('nutrition.foliar.crumb.setup') },
  ]);

  const load = useCallback(async () => {
    try {
      const list = await nutritionService.listProgrammes();
      setProgrammes(list);
      setError('');
    } catch (_err) {
      setProgrammes([]);
      setError(t('nutrition.foliar.setup.loadFailed'));
    }
    // t is intentionally omitted: react-i18next's `t` is not referentially
    // stable, and putting it in a dep array makes the effect re-fire on every
    // render (the S2 month-picker trap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClone = (id: string) => {
    setError('');
    nutritionService
      .cloneProgramme(id)
      .then(() => load())
      .catch(() => setError(t('nutrition.programme.list.cloneError')));
  };

  const handleDelete = (id: string) => {
    // Deliberately no setError('') here: window.confirm below can be
    // cancelled, and clearing before that prompt would silently wipe an
    // unrelated, still-valid error the moment a user opens then dismisses
    // the confirm dialog. load()'s own success path clears `error` on the
    // actual delete's reload, which is the only clear this path needs.
    if (!window.confirm(t('nutrition.programme.list.deleteConfirm'))) return;
    nutritionService
      .deleteProgramme(id)
      .then(() => load())
      .catch((err: { response?: { data?: { code?: string; sessionCount?: number } } }) => {
        // Branch on the CODE, never the message: the server sentence is prose
        // and changes whenever it is reworded.
        const data = err?.response?.data;
        if (data?.code === 'PROGRAMME_HAS_SESSIONS') {
          setError(t('nutrition.programme.list.deleteBlocked', { sessions: data.sessionCount ?? 0 }));
          return;
        }
        setError(t('nutrition.programme.list.deleteError'));
      });
  };

  const openNew = () => {
    setNewSeason('');
    setNewVendor('');
    setNewContact('');
    setCreateError('');
    setShowNew(true);
  };

  const handleCreate = async () => {
    setCreateError('');
    setCreating(true);
    let created: NutritionProgramme | null = null;
    try {
      created = await nutritionService.createProgramme({
        season: newSeason.trim(),
        source: { vendor: newVendor.trim(), contact: newContact.trim() },
      });
    } catch (_err) {
      created = null;
    } finally {
      // Re-fetch the list regardless of outcome — never splice local state.
      // This uses the PAGE-level `error`/load() pair, which is entirely
      // separate from `createError` now, so a load failure here can no
      // longer be overwritten by (or overwrite) the create failure.
      await load();
      // Modal stays open on failure (no setShowNew(false) below) so the
      // user keeps their typed input and can retry.
      if (!created) setCreateError(t('nutrition.foliar.setup.createError'));
      setCreating(false);
    }
    if (created) {
      setShowNew(false);
      navigate(editorPath(created._id));
    }
  };

  // Mirrors FoliarSignOffPage's guard shape: the error branch returns BEFORE
  // the loading guard (a failed load must render a notice, not sit on
  // "loading" forever), and the loading guard itself renders NEITHER the
  // DataTable's emptyState NOR any row — without this, `rows` derives from
  // `programmes || []` and a slow/failed fetch would show "No programs yet."
  // (inviting a duplicate program) or that message stacked with a load-error
  // notice simultaneously. `programmes` only starts null and is never reset
  // to null afterwards, so this guard fires once, on first mount, not on
  // every clone/delete/create reload.
  if (error && programmes === null) {
    return <div className="pat-page"><Notice tone="danger" testId="fst-error">{error}</Notice></div>;
  }
  if (programmes === null) return <div className="pat-page">{t('common.loading')}</div>;

  const rows: SetupRow[] = buildSetupRows(programmes);
  const shown = filterSetupRows(rows, search, statusFilter);

  const chips = [
    { id: 'all', label: t('nutrition.programme.list.filters.all'), active: statusFilter === 'all' },
    { id: 'active', label: t('nutrition.programme.list.filters.active'), active: statusFilter === 'active' },
    { id: 'received', label: t('nutrition.programme.list.filters.received'), active: statusFilter === 'received' },
  ];

  // ONE render function for the row actions menu, used by the column AND the
  // card (GC 8 / defect #456). Stops propagation so a click inside the menu
  // does not also fire the row/card's onRowClick navigation.
  const renderActions = (row: SetupRow, testId: string) => (
    <span data-testid={testId} onClick={(e) => e.stopPropagation()}>
      <NutritionProgramActionsMenu
        programName={row.season}
        programmeId={row.id}
        programmeStatus={row.status}
        onViewDetails={() => navigate(editorPath(row.id))}
        onClone={() => handleClone(row.id)}
        onDelete={() => handleDelete(row.id)}
        onRefresh={load}
      />
    </span>
  );

  const resolvedProgramme = workspace?.programme ?? null;
  const yt = youngTreeSummary(resolvedProgramme);

  return (
    <div className="pat-page" data-testid="foliar-setup-page">
      {error && <Notice tone="danger" testId="fst-error">{error}</Notice>}

      <FormSection
        id="foliar-setup-programmes"
        title={t('nutrition.foliar.setup.programmesCard')}
        description={t('nutrition.foliar.setup.programmesDesc')}
      >
        <div className="ntr-fst-actionbar">
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            data-testid="fst-new-program"
            onClick={openNew}
          >
            {t('nutrition.foliar.setup.newProgram')}
          </button>
        </div>
        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('nutrition.programme.list.searchPlaceholder')}
          chips={chips}
          onChipClick={(chipId) => setStatusFilter(chipId as 'all' | 'active' | 'received')}
        />
        <DataTable<SetupRow>
          rows={shown}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(editorPath(r.id))}
          emptyState={t('nutrition.foliar.setup.empty')}
          columns={[
            {
              key: 'season',
              label: t('nutrition.foliar.setup.col.season'),
              render: (r) => r.season,
            },
            {
              key: 'source',
              label: t('nutrition.foliar.setup.col.source'),
              render: (r) => r.source || '—',
            },
            {
              key: 'status',
              label: t('nutrition.foliar.setup.col.status'),
              render: (r) => (
                <StatusPill variant={STATUS_VARIANT[r.status] ?? 'info'}>
                  {t('nutrition.programme.status.' + r.status)}
                </StatusPill>
              ),
            },
            {
              key: 'rounds',
              label: t('nutrition.foliar.setup.col.rounds'),
              align: 'right',
              render: (r) => r.rounds,
            },
            {
              key: 'orchards',
              label: t('nutrition.foliar.setup.col.orchards'),
              align: 'right',
              render: (r) => r.orchards,
            },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (r) => renderActions(r, `programme-actions-table-${r.id}`),
            },
          ]}
          mobileCardRow={(r) => (
            <div className="ntr-proglist-card">
              <div className="ntr-proglist-card__body">
                <strong>
                  {r.season} · {r.source || '—'}
                </strong>
                <div className="ntr-fst-card-meta">
                  {t('nutrition.foliar.setup.col.rounds')}: {r.rounds} ·{' '}
                  {t('nutrition.foliar.setup.col.orchards')}: {r.orchards} ·{' '}
                  {t('nutrition.programme.status.' + r.status)}
                </div>
              </div>
              <div className="ntr-proglist-card__actions">
                {renderActions(r, `programme-actions-card-${r.id}`)}
              </div>
            </div>
          )}
        />
      </FormSection>

      <FormSection
        id="foliar-setup-youngtree"
        title={t('nutrition.foliar.setup.youngTreeCard')}
        description={t('nutrition.foliar.setup.youngTreeDesc')}
      >
        {!resolvedProgramme ? (
          <Notice tone="info" testId="fst-youngtree-unresolved">
            {t('nutrition.foliar.setup.youngTreeNoProgramme')}
          </Notice>
        ) : yt.present ? (
          <div className="ntr-fst-card">
            <strong>{yt.name}</strong>
            <div className="ntr-chips">
              {yt.productNames.map((name) => (
                <span key={name} className="ntr-chip">{name}</span>
              ))}
            </div>
            <Link
              className="pat-btn pat-btn--subtle"
              data-testid="fst-youngtree-edit"
              to={editorPath(resolvedProgramme._id)}
            >
              {t('nutrition.foliar.setup.youngTreeEdit')}
            </Link>
          </div>
        ) : (
          <div className="ntr-fst-card">
            <span className="ntr-muted">{t('nutrition.foliar.setup.youngTreeNone')}</span>
            <Link
              className="pat-btn pat-btn--primary"
              data-testid="fst-youngtree-add"
              to={editorPath(resolvedProgramme._id)}
            >
              {t('nutrition.foliar.setup.youngTreeAdd')}
            </Link>
          </div>
        )}
      </FormSection>

      <Modal show={showNew} onHide={() => setShowNew(false)} title={t('nutrition.foliar.setup.createTitle')}>
        <Modal.Body>
          {createError && (
            <Notice tone="danger" testId="fst-create-error" className="mb-3">
              {createError}
            </Notice>
          )}
          <div className="pat-field-grid pat-field-grid--2">
            <Field label={t('nutrition.foliar.setup.createSeason')} required controlId="fst-new-season">
              <input
                id="fst-new-season"
                type="text"
                className="pat-input"
                value={newSeason}
                onChange={(e) => setNewSeason(e.target.value)}
              />
            </Field>
            <Field label={t('nutrition.foliar.setup.createVendor')} controlId="fst-new-vendor">
              <input
                id="fst-new-vendor"
                type="text"
                className="pat-input"
                value={newVendor}
                onChange={(e) => setNewVendor(e.target.value)}
              />
            </Field>
            <Field label={t('nutrition.foliar.setup.createContact')} controlId="fst-new-contact">
              <input
                id="fst-new-contact"
                type="text"
                className="pat-input"
                value={newContact}
                onChange={(e) => setNewContact(e.target.value)}
              />
            </Field>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={() => setShowNew(false)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            data-testid="fst-create"
            disabled={!newSeason.trim() || creating}
            onClick={handleCreate}
          >
            {t('nutrition.foliar.setup.create')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FoliarSetupPage;
